import {
  browserName,
  browserVersion,
  deviceType,
  engineName,
  engineVersion,
  fullBrowserVersion,
  getUA,
  isMobile,
  isTablet,
  mobileModel,
  mobileVendor,
  osName,
  osVersion
} from 'mobile-device-detect'
import {
  BASE_WIDTH,
  BillingCycleType,
  ContentWidthDeviceSizeToClass,
  Countries,
  FulfillmentStatus,
  FulfillmentStatusClasses,
  FulfillmentStatusStrings,
  ItemPendingStatus,
  ItemPendingStatusStrings,
  ItemStatus,
  ItemStatusClasses,
  ItemStatusStrings,
  PaymentStatusClasses,
  PaymentStatusStrings,
  ProcessType,
  Regions,
  SubscriptionDisplayType,
  InputType,
  StepButtonType,
  IntentHierarchyMapping,
  IntentHierarchyLabels,
  PopupCloseSetting,
  PreviewType,
  IntentLabels,
  DigitalIntentLabels
} from '@/constants'
import cssVars from 'css-vars-ponyfill'
import router from '@/router'
import store from '@/store'
import VueCookie from 'vue-cookie'

export function getDeviceInfo() {
  let device = deviceType

  if (!device) {
    device = isMobile ? (isTablet ? 'tablet' : 'mobile') : 'desktop'
  }

  return {
    browser_full_version: browserVersion,
    browser_major_version: fullBrowserVersion,
    browser_name: browserName,
    engine_name: engineName,
    engine_version: engineVersion,
    os_name: osName,
    os_version: osVersion,
    device_type: device,
    mobile_vendor: (mobileVendor === 'none') ? null : mobileVendor,
    mobile_model: (mobileModel === 'none') ? null : mobileModel,
    user_agent: getUA
  }
}

const monthNames = [
  'January', 'February', 'March',
  'April', 'May', 'June',
  'July', 'August', 'September',
  'October', 'November', 'December'
]

export function getUTCShortDate(isoDateStr) {
  let date = new Date(isoDateStr)
  let month = date.getUTCMonth() + 1

  return month.toString() + '/' + date.getUTCDate().toString() + '/' + date.getUTCFullYear().toString().slice(-2)
}

export function getShortDate(isoDateStr) {
  let date = new Date(isoDateStr)
  let month = date.getMonth() + 1

  return month.toString() + '/' + date.getDate().toString() + '/' + date.getFullYear().toString().slice(-2)
}

export function getShortHumanReadableUTCDate(isoDateStr) {
  let date = new Date(isoDateStr)
  return `${monthNames[date.getUTCMonth()].slice(0, 3)} ${date.getUTCDate().toString()}, ${date.getUTCFullYear().toString()}`
}

export function getShortHumanReadableDate(isoDateStr) {
  let date = new Date(isoDateStr)
  return `${date.getDate().toString()} ${monthNames[date.getMonth()].slice(0, 3)}, ${date.getFullYear().toString()}`
}

export function getHumanReadableDate(isoDateStr, prefixMonth = null) {
  let date = new Date(isoDateStr)
  if (prefixMonth) {
    return `${monthNames[date.getMonth()]} ${date.getDate().toString()}, ${date.getFullYear().toString()}`
  }
  return `${date.getDate().toString()} ${monthNames[date.getMonth()]}, ${date.getFullYear().toString()}`
}

export function getShortTime(isoDateStr) {
  let date = new Date(isoDateStr)
  let minutes = ('0' + date.getMinutes().toString()).slice(-2)
  let ampm = (date.getHours() >= 12) ? 'pm' : 'am'
  let hours = date.getHours() % 12

  if (hours === 0) {
    hours = 12
  }

  return hours.toString() + ':' + minutes.toString() + ' ' + ampm
}

export function setPlaceholders(placeholders) {
  const placeholderLabels = Array.from(document.querySelectorAll('.placeholder-label'))

  placeholderLabels.forEach(elem => {
    const target = elem.dataset.for

    placeholders[target] = elem.innerText
  })
}

export function getDomain() {
  let parts = window.location.hostname.split('.')

  if (parts.length > 2) {
    parts = parts.slice(-2)
  }

  return parts.join('.')
}

export function getClassFromObject(obj) {
  return (obj && obj.classes) ? obj.classes.join(' ') : ''
}

export function isOutlineButton(input) {
  return input.button_styles &&
    [StepButtonType.FlatOutline, StepButtonType.ModernOutline].indexOf(input.button_styles.style_type) > -1
}

export function getCustomInputClass(session, input, includeButtonClass = false) {
  let classes = getClassFromObject(input)
  if (includeButtonClass) {
    if (!session.is_legacy) {
      classes += 'input-btn mb-0'
      if (isOutlineButton(input)) {
        classes += ' input-outline-btn'
      }
    } else {
      classes += 'text-capitalize'
    }
  }
  return classes
}

export function getLocale() {
  if (typeof Intl.NumberFormat().resolvedOptions === 'function') {
    return Intl.NumberFormat().resolvedOptions().locale
  }
  if (navigator.languages !== undefined) {
    return navigator.languages[0]
  }
  return navigator.language || 'en-US'
}

export function formatCurrency(value, currency = 'USD', minimumFractionDigits = 2) {
  if (value === null || value === undefined || value === '') {
    return value
  }
  if (typeof value !== 'number') {
    value = parseFloat(value)
  }
  const formatter = new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: minimumFractionDigits
  })
  return formatter.format(value)
}

export function getErrorMessage(error) {
  let status = 500
  let msg = ''

  if (error) {
    if (error.status) {
      status = error.status
    } else if (error.response && error.response.status) {
      status = error.response.status
    }

    if (status === 404) {
      msg = error.error ? error.error.message : error.message
    } else if (status >= 500) {
      msg = 'An internal server error occurred'
    } else {
      msg = 'Sorry, but an error occurred'
    }
  }

  return msg
}

export function getErrorData(error) {
  let errorData = error.response
  if (typeof errorData === 'object' && 'data' in errorData) {
    errorData = errorData.data
    if (typeof errorData === 'object' && 'data' in errorData) {
      errorData = errorData.data
    }
  }
  let errors = []
  let msg = ''
  if (typeof errorData === 'string') {
    msg = errorData
    errorData = null
  }

  if (errorData) {
    Object.keys(errorData).forEach(key => {
      if (key === 'error_message') {
        msg = errorData[key]
      } else {
        errors = errors.concat(errorData[key])
      }
    })
  } else {
    msg = error.error ? error.error.message : error.message

    if (msg) {
      errors = [msg]
    }
  }

  return {data: errorData, errors: errors, message: msg, errorString: errorData ? JSON.stringify(errorData) : msg}
}

export function sortItems(items) {
  return items.sort(function(item1, item2) {
    if (item1.order.id === item2.order.id) {
      if (item1.campaign_product.process_type === ProcessType.Fee) {
        return 1 // fees are always last
      } else if (item2.campaign_product.process_type === ProcessType.Fee) {
        return -1
      } else if (item1.campaign_product.process_type === ProcessType.Installment) {
        return 1 // then installments
      } else if (item2.campaign_product.process_type === ProcessType.Installment) {
        return -1
      } else if (item1.campaign_product.process_type === ProcessType.Warranty) {
        return 1 // then warranties
      } else if (item2.campaign_product.process_type === ProcessType.Warranty) {
        return -1
      } else if (item1.campaign_product.id === item2.campaign_product.id) {
        if (item1.fulfillment_status === FulfillmentStatus.NextCycle) {
          return 1
        } else {
          return -1
        }
      } else if (item1.fulfillment_status !== item2.fulfillment_status) {
        // sort order items by status - non shippable and cancelled items are last
        if (item1.fulfillment_status === FulfillmentStatus.Cancelled ||
          item1.fulfillment_status === FulfillmentStatus.Removed
        ) {
          return 1
        } else if (item2.fulfillment_status === FulfillmentStatus.Cancelled ||
          item2.fulfillment_status === FulfillmentStatus.Removed
        ) {
          return -1
        } else if (item1.fulfillment_status === FulfillmentStatus.None) {
          return 1
        } else if (item2.fulfillment_status === FulfillmentStatus.None) {
          return -1
        }
      } else {
        // sort shippable items by type - upsells are last
        if (item1.campaign_product.product_type !== item2.campaign_product.product_type) {
          return (item1.campaign_product.product_type > item2.campaign_product.product_type) ? 1 : -1
        }
      }
    } else {
      // sort orders by date (newest order 1st)
      const d1 = new Date(item1.order.created)
      const d2 = new Date(item2.order.created)

      return d2 - d1
    }

    return 0
  })
}

export function isInitialHoldStatus(item) {
  if (item.status === ItemStatus.Pending) {
    return true
  } else if (item.status === ItemStatus.Paused) {
    return false // we always show paused subscription
  }

  return (item.billing_cycle === 1) && (item.fulfillment_status === FulfillmentStatus.Hold)
}

export function isRecurring(item) {
  return item.billing_cycle_type === BillingCycleType.Recurring || item.campaign_product.third_party_recurring
}

export function showSubscription(item) {
  if (isPendingCancelSubscription(item) && getPendingFulfillmentStatus(item) === ItemPendingStatus.Cancel) {
    return false // we don't need to show both cancel pending and cancel subscription pending
  }

  return isRecurring(item) &&
    (item.status !== ItemStatus.Pending) &&
    (
      (item.status === ItemStatus.Cancelled) ||
      isPendingCancelSubscription(item) ||
      (
        (item.campaign_product.subscription_display_type !== SubscriptionDisplayType.Never) &&
        ((item.campaign_product.subscription_display_type === SubscriptionDisplayType.Always) ||
          (item.latest_billing_cycle > 1))
      )
    ) &&
    (item.billing_cycle === item.latest_billing_cycle)
}

export function showFulfillment(item) {
  if ((item.fulfillment_status !== FulfillmentStatus.NextCycle) &&
    (item.fulfillment_status !== FulfillmentStatus.None) &&
    (item.fulfillment_status !== FulfillmentStatus.ParentOrder)) {
    return true
  }

  return (item.status === ItemStatus.Declined ||
    item.status === ItemStatus.Cancelled ||
    item.status === ItemStatus.ThirdPartyCancel ||
    getPendingFulfillmentStatus(item) !== ItemPendingStatus.NotPending
  ) && !showSubscription(item)
}

export function getItemStatus(item, statusLabels, appendSubscription = true) {
  if (isPendingCancelSubscription(item)) {
    return appendSubscription ? 'Cancel Subscription Pending' : 'Cancel Pending'
  }

  if (appendSubscription) {
    if ((item.billing_cycle_type === BillingCycleType.Recurring) ||
      (item.billing_cycle_type === BillingCycleType.RecurringChangeProduct) ||
      item.campaign_product.third_party_recurring
    ) {
      if ((item.status !== ItemStatus.Trial) || (item.final_billing_cycle !== 2)) {
        return 'Subscription ' + ItemStatusStrings[item.status]
      }
    }
  }

  return statusLabels[item.status] || ItemStatusStrings[item.status]
}

export function getItemStatusClass(item) {
  return isPendingCancelSubscription(item) ? 'pending' : ItemStatusClasses[item.status]
}

export function getPaymentStatus(order) {
  return PaymentStatusStrings[order.payment_status]
}

export function getPaymentStatusClass(order) {
  return PaymentStatusClasses[order.payment_status]
}

function isPendingCancelSubscription(item) {
  if (item.pending_statuses) {
    for (let i = 0; i < item.pending_statuses.length; i++) {
      if (item.pending_statuses[i] === ItemPendingStatus.CancelSubscription) {
        return true
      }
    }
  }

  return false
}

function getPendingFulfillmentStatus(item) {
  if (item.pending_statuses) {
    for (let i = 0; i < item.pending_statuses.length; i++) {
      if (item.pending_statuses[i] !== ItemPendingStatus.CancelSubscription) {
        return item.pending_statuses[i]
      }
    }
  }

  return ItemPendingStatus.NotPending
}

export function getItemFulfillmentStatus(item) {
  const pendingStatus = getPendingFulfillmentStatus(item)

  if (pendingStatus !== ItemPendingStatus.NotPending) {
    return ItemPendingStatusStrings[pendingStatus]
  }

  if (item.status === ItemStatus.Declined) {
    return 'Declined'
  }

  if ((item.status === ItemStatus.Cancelled || item.status === ItemStatus.ThirdPartyCancel) &&
    item.fulfillment_status === FulfillmentStatus.None) {
    return 'Cancelled'
  }

  return FulfillmentStatusStrings[item.fulfillment_status]
}

export function getItemFulfillmentStatusClass(item) {
  if (getPendingFulfillmentStatus(item) !== ItemPendingStatus.NotPending) {
    return 'pending'
  }

  if (item.status === ItemStatus.Declined) {
    return 'cancelled'
  }

  if ((item.status === ItemStatus.Cancelled || item.status === ItemStatus.ThirdPartyCancel) &&
    item.fulfillment_status === FulfillmentStatus.None) {
    return 'cancelled'
  }

  return FulfillmentStatusClasses[item.fulfillment_status]
}

export function getDefaultProductImage(product) {
  if (product.default_image) {
    return product.default_image
  } else if (product.images.length > 0) {
    return product.images[0]
  }

  return null
}

export function getPlaceholderImageUrl(size) {
  const imageSizes = {
    small: [100, 100],
    medium: [300, 300],
    large: [400, 400],
    original: [400, 400]
  }
  const width = imageSizes[size][0] || 400
  const height = imageSizes[size][1] || 400

  return 'https://placehold.co/' + width.toString() + 'x' + height.toString() + '?text=No+Image'
}

export function getDefaultProductImageUrl(product, size) {
  const image = getDefaultProductImage(product)

  if (image) {
    return image.file[size] || image.file.original
  }

  return getPlaceholderImageUrl(size)
}

export function getCampaignProductImageUrl(campaignProduct, size, product = null) {
  if (campaignProduct.image && campaignProduct.image.file) {
    return campaignProduct.image.file[size] || campaignProduct.image.file.original
  }

  if (!product) {
    product = campaignProduct.product

    if (!product) {
      return getPlaceholderImageUrl(size)
    }
  }

  return getDefaultProductImageUrl(product, size)
}

export function setFallbackImageUrl(event, item, size, isItem = false, useSubscription = false) {
  event.error = null
  if (!event.currentTarget.setOriginalImage) {
    event.currentTarget.setOriginalImage = true
    if (!isItem) {
      event.target.src = getCampaignProductImageUrl(item, 'original')
    } else {
      event.target.src = getItemImageUrl(item, 'original', useSubscription)
    }
  } else {
    delete event.currentTarget['setOriginalImage']
    event.target.src = getPlaceholderImageUrl(size)
  }
}

export function getItemImageUrl(item, size, useSubscription = false) {
  let variant = null
  let product = null

  if (useSubscription) {
    product = item.next_cycle_product
    variant = item.next_variant
  }

  if (!product) {
    product = item.product || item.campaign_product.product
  }

  if (!variant) {
    variant = item.variant
  }

  if (variant && variant.image) {
    return variant.image.file[size] || variant.image.file.original
  }

  if (product.id === item.campaign_product.product_id) {
    return getCampaignProductImageUrl(item.campaign_product, size, product)
  }

  return getDefaultProductImageUrl(product, size)
}

export function getExpDate(isoDateStr, forCustomerPortal = false) {
  let date = new Date(isoDateStr)
  let month = date.getMonth() + 1

  if (forCustomerPortal) {
    return ('0' + month.toString()).slice(-2) + ' / ' + date.getFullYear().toString()
  }

  return ('0' + month.toString()).slice(-2) + '/' + date.getFullYear().toString().slice(-2)
}

export function isTestCustomer(customer) {
  return (customer && customer.email && customer.email.toLowerCase().endsWith('@solvpath.com'))
}

export function isTestCard(cardNumber, session, customer) {
  if (session && session.customer && session.customer.is_test) {
    return true
  }

  if (isTestCustomer(customer)) {
    return true
  }

  let testCards = process.env.VUE_APP_TEST_CARDS ? process.env.VUE_APP_TEST_CARDS.split(',') : []
  return testCards.indexOf(cardNumber) > -1
}

export function setFirstInputDone(flag = true) {
  let value = flag ? 'true' : 'false'
  sessionStorage.setItem('firstInputDone', value)
}

export function isFirstInputDone() {
  return sessionStorage.getItem('firstInputDone') === 'true'
}

export function setOrderDetail(value) {
  sessionStorage.setItem('orderDetail', JSON.stringify(value))
}

export function removeOrderDetail() {
  sessionStorage.removeItem('orderDetail')
}

export function getOrderDetail() {
  return JSON.parse(sessionStorage.getItem('orderDetail'))
}

export function calculatePrice(item, session) {
  let price = item.campaign_product.session_price

  if (session && session.has_membership && item.campaign_product.membership_price !== null) {
    price = item.campaign_product.membership_price
  } else if (item.variant && item.variant.price) {
    price = item.variant.price
  }

  if (price === null || price === undefined) {
    price = item.campaign_product.price
  }
  // convert price to number for the last time
  price = Number(price)

  return price * item.quantity
}

export function applyFont(attribute) {
  let nodeList = Array.from(document.querySelectorAll(`[${attribute}]`))
  if (nodeList.length > 0) {
    nodeList.forEach(element => {
      let font = element.getAttribute(attribute)
      element.style.fontSize = font
    })
  }
}

export function checkFontScreenSize(mediaWidth) {
  if (mediaWidth.matches) {
    applyFont('data-mce-font-mobile')
  } else {
    applyFont('data-mce-font-desktop')
  }
}

export function checkAndApplyFontSize() {
  let mediaQuery = window.matchMedia('(max-width: 768px)')
  checkFontScreenSize(mediaQuery)
  try {
    mediaQuery.addEventListener('change', checkFontScreenSize)
  } catch (e) {
    // deprecated, but still works in safari
    mediaQuery.addListener(checkFontScreenSize)
  }
}

export function getReCaptchaHeaders(params) {
  let headers = {}
  if ('recaptcha' in params) {
    headers['recaptcha'] = params['recaptcha']
    headers['recaptcha-action'] = params['recaptcha_action']
    delete params['recaptcha']
    delete params['recaptcha_action']
  }
  return [params, headers]
}

export function isEmbedded() {
  let result = false

  try {
    result = window.location !== window.parent.location
  } catch (e) {
    result = true // exception thrown due to cross origin, so must be embedded
  }

  return result
}

export function scrollToTop() {
  document.body.scrollTop = 0 // For Safari
  document.documentElement.scrollTop = 0 // For Chrome, Firefox, IE and Opera

  if (isEmbedded()) {
    window.parent.postMessage({ type: 'scrollToTop' }, '*')
  }
}

export function getItemVariants(item) {
  if (item.variant) {
    let options = []
    item.variant.options.forEach(option => {
      if (!option.is_deleted) {
        options.push(option.name + ': ' + option.value)
      }
    })

    return options.join(', ')
  }

  return ''
}

function parseCssBlocks(cssText) {
  const blocks = []
  const regex = /([^{]+){([^}]+)}/g
  let match
  while ((match = regex.exec(cssText))) {
    const selector = match[1].trim()
    const body = match[2].trim()
    const props = {}

    body.split(';').forEach(line => {
      if (!line.trim()) return
      const [key, value] = line.split(':')
      if (key && value) {
        props[key.trim()] = value.trim()
      }
    })

    blocks.push({ selector, props })
  }
  return blocks
}

function getFromValue(prop, toValue) {
  if (['width', 'height'].includes(prop)) {
    // keep the same unit (px, %, em, etc.)
    const unit = toValue.replace(/[0-9.]/g, '') || 'px'
    return `0${unit}`
  }
  const propValues = {
    opacity: '0',
    'background-color': 'transparent',
    'box-shadow': 'none',
    transform: 'none'
  }

  if (prop in propValues) {
    return propValues[prop]
  }

  return 'unset'
}

export function addNewStyles(id, styles) {
  // Remove old style
  const old = document.getElementById(id)

  if (old) {
    old.remove()
  }

  // Parse blocks
  const blocks = parseCssBlocks(styles)
  let transformed = styles
  let animations = []
  const transitionRegex = /transition:\s*([^;]+);?/g

  transformed = transformed.replace(transitionRegex, (match, transitionDef, offset) => {
    // Find which block this transition belongs to
    const currentBlock = blocks.find(b => styles.indexOf(b.selector) < offset && offset < styles.indexOf('}', styles.indexOf(b.selector)))

    const parts = transitionDef.split(',').map(p => p.trim())
    const anims = parts.map((p, i) => {
      const tokens = p.split(/\s+/)
      const prop = tokens[0]
      const duration = tokens[1] || '1s'
      const easing = tokens[2] || 'ease'

      const toValue = currentBlock.props[prop] || 1
      if (!toValue) return null

      const animName = `${prop.replace(/-/g, '_')}Anim${i}`
      animations.push({ prop, animName, toValue })

      return `${animName} ${duration} ${easing} forwards`
    }).filter(Boolean)

    return `animation: ${anims.join(', ')};`
  })

  // Add keyframes dynamically
  animations.forEach(({ prop, animName, toValue }) => {
    // Default `from` is unset (or could be transparent/0)
    transformed += `
      @keyframes ${animName} {
        from { ${prop}: ${getFromValue(prop, toValue)}; }
        to   { ${prop}: ${toValue}; }
      }
    `
  })

  const styleEl = document.createElement('style')
  styleEl.type = 'text/css'
  styleEl.id = id
  styleEl.appendChild(document.createTextNode(transformed))
  document.head.appendChild(styleEl)
}

export function dateDifference(date1, date2) {
  const diffInMs = Math.abs(new Date(date2) - new Date(date1))

  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))

  return {
    diffInMinutes,
    diffInHours
  }
}

export function isCountryUSOrCA(country) {
  if (country) {
    country = country.toUpperCase()
  }

  return ['US', 'CA'].indexOf(country) > -1
}

export function formatZipField(value, country) {
  if (country === 'US') {
    return value.substring(0, 5)
  }

  return value
}

export function getCountryStates(country, blacklistedStates) {
  let states = []

  if (country) {
    country = country.toUpperCase()

    if (country in Regions) {
      states = [{value: null, text: 'State', disabled: true}]
      Regions[country].forEach(state => {
        states.push({...state,
          disabled: blacklistedStates && blacklistedStates.length > 0 && blacklistedStates.indexOf(state.value) > -1})
      })
    }
  }

  return states
}

function isMexicanRFCField(field, country, isShipping = false) {
  return !isShipping && (country === 'MX') && (field === 'company')
}

export function updateRequiredFields(requiredFields, country, isShipping = false) {
  let countriesRequiringState = ['US', 'CA', 'MX']

  if (country) {
    country = country.toUpperCase()

    if (countriesRequiringState.indexOf(country) > -1) {
      requiredFields.push('state')
    }

    if (isMexicanRFCField('company', country, isShipping)) {
      requiredFields.push('company')
    }
  }
}

export function isFieldRequired(field, country, isShipping = false) {
  if (country) {
    country = country.toUpperCase()

    if (isMexicanRFCField(field, country, isShipping)) {
      return true
    }
  }

  return false
}

export function getFieldLabel(field, defaultLabel, country, isShipping = false) {
  if (country) {
    country = country.toUpperCase()

    if (isMexicanRFCField(field, country, isShipping)) {
      return 'RFC'
    }
  }

  return defaultLabel
}

function getDeviceWidth() {
  return window.innerWidth
}

function calculateScaleMultiplier(deviceWidth) {
  const MIN_WIDTH = 320 // Minimum screen width for scaling
  const MAX_WIDTH = 8000 // Maximum screen width for scaling
  const MIN_MULTIPLIER = 1.1 // Minimum scale multiplier
  const MAX_MULTIPLIER = 1.6 // Maximum scale multiplier

  // Clamp deviceWidth between minWidth and maxWidth
  deviceWidth = Math.max(MIN_WIDTH, Math.min(deviceWidth, MAX_WIDTH))

  // Calculate proportional multiplier
  return MIN_MULTIPLIER + ((deviceWidth - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH)) * (MAX_MULTIPLIER - MIN_MULTIPLIER)
}

function calculateFontSize(baseFontSize) {
  const deviceWidth = getDeviceWidth()
  if (baseFontSize) {
    baseFontSize = parseFloat(baseFontSize)
    const scaleMultiplier = calculateScaleMultiplier(deviceWidth) // Maximum font size multiplier
    let scaledFontSize = (baseFontSize / BASE_WIDTH) * deviceWidth
    if (deviceWidth < BASE_WIDTH) {
      scaledFontSize = Math.floor(scaledFontSize - 1.5)
    }
    const maxFontSize = baseFontSize * scaleMultiplier // Dynamic max font size
    return Math.min(scaledFontSize, maxFontSize) // Cap at maxFontSize
  }
}

export function updateFontSizes(html) {
  let updatedHtml = html || ''

  if (updatedHtml) {
    const fontSizeRegex = /font-size:\s*((\d+(\.\d+)?)px);/

    let match
    while ((match = fontSizeRegex.exec(html)) !== null) {
      const originalFontSize = match[1]
      const newFontSize = calculateFontSize(originalFontSize)
      updatedHtml = updatedHtml.replaceAll(match[0], 'font-size: ' + newFontSize + 'px;')
      html = html.slice(match.index + 1)
    }
  }

  return updatedHtml
}

export function recalculateFontSizes() {
  document.querySelectorAll('[data-fontsize]').forEach((element) => {
    const dataFontSize = element.getAttribute('data-fontsize')
    if (dataFontSize) {
      element.style.fontSize = calculateFontSize(dataFontSize) + 'px;'
    }
  })
}

export function getStates(campaign, country = Regions.US) {
  let states = [{value: null, text: 'State', disabled: true}]
  Regions[country].forEach(state => {
    states.push({...state,
      disabled: campaign.blacklisted_states && campaign.blacklisted_states.length > 0 &&
        campaign.blacklisted_states.indexOf(state.value) > -1})
  })

  return states
}

export function getCountries(session) {
  let allowedCountries = session.campaign.allowed_countries
  let countries = []

  if (!allowedCountries || allowedCountries.length === 0) {
    // if no countries are allowed, we will use all countries
    allowedCountries = Object.keys(Countries)
  }

  if (session.customer && session.campaign.allowed_countries) {
    // make sure to include the customer's current country
    if (session.customer.country &&
      session.customer.country in Countries &&
      allowedCountries.indexOf(session.customer.country) < 0
    ) {
      allowedCountries.push(session.customer.country)
    }

    if (session.customer.ship_country &&
      session.customer.ship_country !== session.customer.country &&
      session.customer.ship_country in Countries &&
      allowedCountries.indexOf(session.customer.ship_country) < 0
    ) {
      allowedCountries.push(session.customer.ship_country)
    }
  }

  allowedCountries.forEach(country => {
    countries.push({value: country, text: Countries[country]})
  })

  countries.sort(function(a, b) {
    if (a.value === 'US') {
      return -1
    } else if (b.value === 'US') {
      return 1
    }
    return a.text.localeCompare(b.text)
  })

  return countries
}

export function toggleBodyScrollbar(visible) {
  const body = document.getElementsByTagName('body')[0]

  if (visible) {
    body.classList.add('overflow-hidden')
  } else {
    body.classList.remove('overflow-hidden')
  }
}

export function prepareCartItems(cart, params) {
  return params.products.filter((product) => {
    const campaignProduct = product.campaign_product || product
    let cartProduct = cart.find((item) => item.campaign_product.id === campaignProduct.id)
    if (cartProduct && campaignProduct.max_quantity >= cartProduct.quantity) {
      return false
    }
    return product
  }).map((product) => {
    const campaignProduct = product.campaign_product || product
    return {
      quantity: 1,
      variant: product.variant,
      buy_now_clicked: params.checkout,
      campaign_product: campaignProduct
    }
  })
}

export function getIntentLabel(item, intent) {
  if (!item.is_shippable && (intent in DigitalIntentLabels)) {
    return DigitalIntentLabels[intent]
  }
  return IntentLabels[intent]
}

export function getItemIntents(item, settings, addOrderTrackingIntent = true) {
  let intents = []
  if (settings.show_offer_details && item.can_show_offer_details) {
    intents.push(InputType.OfferDetails)
  }
  if (addOrderTrackingIntent && settings.show_tracking_in_list && item.can_track_shipment) {
    intents.push(InputType.ShipmentTracking)
  }
  if (item.status === ItemStatus.Trial) {
    if (item.can_cancel_trial) {
      if (isInitialHoldStatus(item)) {
        if (settings.allow_cancel_trial) {
          intents.push(InputType.CancelTrialHold)
        }
      } else {
        if (settings.allow_cancel_subscription_trial) {
          intents.push(InputType.CancelTrial)
        }
      }
    }
    if (settings.allow_cancel_subscription_trial && item.can_return) {
      intents.push(InputType.ReturnTrial)
    }
  } else {
    if (item.can_change_variant) {
      intents.push(InputType.ChangeVariant)
    }
    if (settings.allow_revision && item.can_revise_package) {
      intents.push(InputType.ChangeProduct)
    }
    if (isRecurring(item)) {
      if (settings.allow_edit_billing_interval && item.can_change_billing_interval) {
        intents.push(InputType.ChangeInterval)
      }
      if (item.can_change_subscription_variant) {
        intents.push(InputType.ChangeSubscriptionVariant)
      }
      if (settings.allow_revise_subscription && item.can_revise_subscription_package) {
        intents.push(InputType.ChangeSubscriptionProduct)
      }
      if (settings.allow_reactivate_subscription && item.can_reactivate_subscription) {
        intents.push(InputType.ReactivateSubscription)
      }
      if (settings.allow_pause_subscription && item.can_pause_subscription) {
        intents.push(InputType.SkipSubscription)
        intents.push(InputType.PauseSubscription)
      }
      if (settings.allow_cancel_subscription && item.can_cancel_subscription &&
        item.billing_cycle >= settings.min_billing_cycle_for_cancel_subscription) {
        // we will show cancel subscription button in any case if product is recurring
        intents.push(InputType.CancelSubscription)
      }

      // if product is recurring, and we can cancel the order, we will show cancel shipment button
      if (settings.allow_cancel && item.can_cancel && item.billing_cycle >= settings.min_billing_cycle_for_cancel) {
        if (item.billing_cycle > 1) {
          intents.push(InputType.CancelRebillHold)
        } else {
          intents.push(InputType.CancelSubscriptionHold)
        }
      }
    } else {
      if (settings.allow_cancel && item.can_cancel) {
        intents.push(InputType.Cancel)
      }
    }

    if (item.can_return) {
      if (isRecurring(item) && item.status === ItemStatus.Active) {
        intents.push(InputType.ReturnSubscription)
      } else {
        intents.push(InputType.Return)
      }
    }
  }

  return intents
}

export function isPrimaryIntent(intent) {
  return intent === InputType.ReactivateSubscription
}

export function groupIntentsByHierarchy(intents) {
  const directIntents = []
  const hierarchyGroups = {}
  const hierarchyOrder = [] // Track order based on first appearance

  intents.forEach(intent => {
    if (intent in IntentHierarchyMapping) {
      const category = IntentHierarchyMapping[intent]

      if (!hierarchyGroups[category]) {
        hierarchyGroups[category] = {
          category,
          label: IntentHierarchyLabels[category],
          intents: []
        }
        hierarchyOrder.push(category)
      }

      hierarchyGroups[category].intents.push(intent)
    } else {
      directIntents.push(intent)
    }
  })

  // Return hierarchy groups in order of first appearance
  const orderedHierarchyGroups = hierarchyOrder.map(category => hierarchyGroups[category])

  return {
    directIntents,
    hierarchyGroups: orderedHierarchyGroups
  }
}

export function isItemSelectionInput(input) {
  return input && ([InputType.Item, InputType.ActiveItem, InputType.SubscriptionItem].indexOf(input.type) >= 0)
}

export function setContentWidth(contentWidth) {
  let widthVariables = {
    label_lg_width: '100%',
    label_md_width: '100%',
    label_sm_width: '100%',
    content_lg_width: '100%',
    content_md_width: '100%',
    content_sm_width: '100%',
    static_image_lg_width: '100%',
    static_image_md_width: '100%',
    static_image_sm_width: '100%',
    accept_button_lg_width: '100%',
    accept_button_md_width: '100%',
    accept_button_sm_width: '100%',
    reject_button_lg_width: '100%',
    reject_button_md_width: '100%',
    reject_button_sm_width: '100%'
  }
  let fields = ['label', 'content', 'static_image', 'accept_button_content', 'reject_button_content']

  if (contentWidth && Object.keys(contentWidth).length) {
    fields.forEach(field => {
      let deviceWidths = contentWidth[field]
      if (deviceWidths) {
        Object.keys(deviceWidths).forEach(device => {
          if (device in ContentWidthDeviceSizeToClass) {
            let width = deviceWidths[device]
            let cssClass = ContentWidthDeviceSizeToClass[device]
            if (width) {
              widthVariables[`${field.replace('_content', '')}_${cssClass}_width`] = `${width}%`
            }
          }
        })
      }
    })
  }
  cssVars({
    variables: widthVariables
  })
}

export function getBundleQuantity(item, useSubscription = false) {
  const bundleQuantity = useSubscription ? item.next_bundle_quantity : item.campaign_product.bundle_quantity
  const quantity = useSubscription ? item.next_quantity || item.quantity : item.quantity
  return quantity * bundleQuantity
}

export function getBundleUnitPrice(item, useSubscription = false) {
  const bundleQuantity = useSubscription ? item.next_bundle_quantity : item.campaign_product.bundle_quantity
  let price = useSubscription ? item.unit_recurring_price : item.unit_price

  if (typeof price !== 'number') {
    price = parseFloat(price)
  }

  return price / bundleQuantity
}

export function stopMouseflowRecording() {
  if (window.mouseflow && window.mouseflow.isRecording()) {
    window.mouseflow.stop()
  }
}

export function getTrimmedValue(value) {
  return (value || '').toLowerCase()
    .replaceAll('-editor', '')
    .replaceAll('-updated', '')
    .replaceAll('updated', '')
    .replaceAll('editor', '')
    .replace(/\([^)]*\)/g, '').trim()
}

export function getStepInfo(session) {
  const label = (new DOMParser().parseFromString(session.label, 'text/html')).body.textContent
  return {
    id: session.step_id,
    slug: getTrimmedValue(session.step_slug),
    name: getTrimmedValue(session.step_name || label || 'Back')
  }
}

export function selectLanguage(code) {
  if (code !== store.state.currentLanguage) {
    // set the new translation
    VueCookie.delete('googtrans', {domain: window.location.hostname})
    VueCookie.delete('googtrans', {domain: getDomain()})
    VueCookie.set('googtrans', '/en/' + code)
    store.commit('set_language', code)
    const query = Object.assign({}, router.currentRoute.query || {})

    if (query.language) {
      delete query.language
      router.replace({'query': query})
    }

    if (store.getters.isSessionActive) {
      store.dispatch('update_session', {language: code, locale: getLocale()})
        .then(result => {
          router.go(0) // refresh
        })
    }
  }
}

export function closeLifeline() {
  store.dispatch('close')
    .then(session => {
      console.log('Closed the lifeline')

      if (store.getters.mustLogoutSession) {
        store.commit('logout')
      }

      router.go(0) // refresh
    })
    .catch(err => {
      console.log(err)
      store.commit('critical_error', err)
    })
}

export function formatDateString(dateStr, addTimeRange = false, onlyDate = false) {
  const date = new Date(dateStr)
  const now = new Date()
  const oneDay = 24 * 60 * 60 * 1000

  const diffDays = Math.floor(
    (new Date(date.getFullYear(), date.getMonth(), date.getDate()) -
      new Date(now.getFullYear(), now.getMonth(), now.getDate())) / oneDay
  )

  let dayLabel
  if (diffDays === 0) {
    dayLabel = 'Today'
  } else if (diffDays === 1) {
    dayLabel = 'Tomorrow'
  } else {
    dayLabel = date.toLocaleDateString(undefined, { weekday: 'long' })
  }

  const month = date.toLocaleDateString(undefined, { month: 'long' })
  const day = date.toLocaleDateString(undefined, { day: 'numeric' })
  let time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
  let result = `${dayLabel}, ${month} ${day}`

  if (addTimeRange) {
    date.setMinutes(date.getMinutes() + 30)
    date.setMinutes(0, 0, 0)
    time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
    date.setHours(date.getHours() + 3)
    const futureTime = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
    result += ` between ${time} - ${futureTime}`
  } else if (!onlyDate) {
    result += ` at ${time}`
  }
  return result
}

export function canShowCloseIcon(session) {
  if (session &&
    session.is_popup &&
    !session.is_background_session &&
    session.popup_close_setting &&
    session.popup_close_setting !== PopupCloseSetting.NoClose
  ) {
    if (session.popup_close_setting === PopupCloseSetting.Undo) {
      return session.can_undo || session.preview_type === PreviewType.Step
    } else if (session.valid_inputs && session.valid_inputs.length) {
      return true
    }
  }

  return false
}
